import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getActiveTestimonials, TestimonialItem } from '../../services/testimonialsService';

// 兼容旧格式的testimonial类型
interface LegacyTestimonial {
    body: {
        cn: string;
        en: string;
    };
    author: {
        name: string;
        role: string;
    };
}

const Testimonials: React.FC = () => {
    const { t, i18n } = useTranslation();
    const sectionRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    
    // 动态数据状态
    const [testimonials, setTestimonials] = useState<LegacyTestimonial[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // 获取推荐数据
    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getActiveTestimonials();
                
                // 转换数据格式以兼容现有UI
                const convertedData: LegacyTestimonial[] = data.map((item: TestimonialItem) => ({
                    body: {
                        cn: item.bodyCn,
                        en: item.bodyEn
                    },
                    author: {
                        name: item.authorName,
                        role: item.authorRole
                    }
                }));
                
                setTestimonials(convertedData);
            } catch (err) {
                console.error('获取推荐数据失败:', err);
                setError('加载推荐内容失败，请稍后重试');
                // 如果加载失败，使用默认数据
                setTestimonials([
                    {
                        body: {
                            cn: "我对这个课后班非常满意。自从我女儿参加以来，她的中文水平有了显著的提高，她还学会了用中文和她的奶奶交流。",
                            en: "I am very pleased with the after-school program. Since enrolling my daughter, her Chinese has improved significantly."
                        },
                        author: {
                            name: "王丽华 Lisa Wang",
                            role: "学生家长 Parent"
                        }
                    }
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, []);

    // 检测组件可见性
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    // 自动轮播功能
    // useEffect(() => {
    //     if (isVisible) {
    //         intervalRef.current = setInterval(() => {
    //             setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    //         }, 5000);
    //     }

    //     return () => {
    //         if (intervalRef.current) {
    //             clearInterval(intervalRef.current);
    //         }
    //     };
    // }, [isVisible]);

    // 手动切换到下一个推荐
    const nextTestimonial = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);

        // 重置自动轮播计时器
        // if (intervalRef.current) {
        //     clearInterval(intervalRef.current);
        // }
        // intervalRef.current = setInterval(() => {
        //     setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
        // }, 5000);
    };

    // 手动切换到上一个推荐
    const prevTestimonial = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);

        // 重置自动轮播计时器
        // if (intervalRef.current) {
        //     clearInterval(intervalRef.current);
        // }
        // intervalRef.current = setInterval(() => {
        //     setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
        // }, 5000);
    };

    const testimonialVariants = {
        enter: (direction: number) => {
            return {
                x: direction > 0 ? '100%' : '-100%',
                opacity: 0
            };
        },
        center: {
            x: 0,
            opacity: 1
        },
        exit: (direction: number) => {
            return {
                x: direction < 0 ? '100%' : '-100%',
                opacity: 0
            };
        }
    };

    // 记录滑动方向
    const [direction, setDirection] = useState(1);

    // 更新索引的函数，同时设置方向
    const updateIndex = (newIndex: number) => {
        setDirection(newIndex > currentIndex ? 1 : -1);
        setCurrentIndex(newIndex);
    };

    // 如果正在加载或没有数据，显示默认内容
    if (loading) {
        return (
            <div className="py-24 sm:py-32 relative" ref={sectionRef}>
                <div className="absolute inset-0 bg-blue-800"></div>
                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="animate-pulse">
                            <div className="h-8 bg-blue-600 rounded-full w-32 mx-auto mb-6"></div>
                            <div className="h-12 bg-blue-700/50 rounded-lg w-full max-w-2xl mx-auto mb-8"></div>
                            <div className="h-96 bg-blue-700/30 rounded-3xl"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (testimonials.length === 0) {
        return (
            <div className="py-24 sm:py-32 relative" ref={sectionRef}>
                <div className="absolute inset-0 bg-blue-800"></div>
                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-2xl text-center mx-auto">
                        <h2 className="text-base font-bold leading-7 text-white bg-blue-600 inline-block px-4 py-1 rounded-full shadow-lg">
                            {t('testimonials.title')}
                        </h2>
                        <p className="mt-6 text-xl text-white/80">
                            暂无用户推荐内容
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="py-24 sm:py-32 relative" ref={sectionRef}>
            {/* 背景装饰 - 纯蓝色调 */}
            <div className="absolute inset-0 bg-blue-800"></div>

            {/* 装饰元素 - 背景几何图形 */}
            <div className="absolute inset-0 z-0 opacity-10">
                <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-blue-400"></div>
                <div className="absolute top-40 -left-20 w-64 h-64 rounded-full bg-blue-500"></div>
                <div className="absolute bottom-20 right-40 w-80 h-80 rounded-full bg-blue-600"></div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                    className="max-w-2xl text-center mx-auto"
                    initial={{ opacity: 0, y: 30 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                    transition={{ duration: 0.5 }}
                >
                    <motion.h2
                        className="text-base font-bold leading-7 text-white bg-blue-600 inline-block px-4 py-1 rounded-full shadow-lg"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                    >
                        {t('testimonials.title')}
                    </motion.h2>
                    <motion.p
                        className="mt-6 text-3xl font-extrabold tracking-tight text-white sm:text-4xl drop-shadow-lg"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <span className="inline-block relative">
                            <span className="relative z-10">{t('testimonials.subtitle')}</span>
                            <span className="absolute bottom-2 left-0 w-full h-3 bg-blue-400/40 -z-10 transform -rotate-1"></span>
                        </span>
                    </motion.p>
                </motion.div>

                {/* 推荐轮播卡片 */}
                <div className="mx-auto mt-16 max-w-4xl relative">
                    {/* 轮播控制按钮 */}
                    <div className="absolute -left-4 top-1/2 transform -translate-y-1/2 z-20">
                        <button
                            onClick={prevTestimonial}
                            className="bg-white/20 hover:bg-white/30 text-white rounded-full p-3 backdrop-blur-sm transition-all duration-300 shadow-lg"
                            aria-label="上一个"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                    </div>

                    <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 z-20">
                        <button
                            onClick={nextTestimonial}
                            className="bg-white/20 hover:bg-white/30 text-white rounded-full p-3 backdrop-blur-sm transition-all duration-300 shadow-lg"
                            aria-label="下一个"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    {/* 轮播内容区域 */}
                    <div className="relative overflow-hidden rounded-3xl h-[500px]">
                        <AnimatePresence initial={false} custom={direction} mode="wait">
                            <motion.div
                                key={currentIndex}
                                custom={direction}
                                variants={testimonialVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{
                                    x: { type: "spring", stiffness: 300, damping: 30 },
                                    opacity: { duration: 0.2 }
                                }}
                                className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-600/80 to-blue-700/80 backdrop-blur-lg p-8 flex flex-col justify-center shadow-2xl ring-1 ring-white/20"
                            >
                                <div className="absolute top-0 left-0 w-full h-full bg-white/5 pointer-events-none rounded-3xl"></div>
                                <div className="flex flex-col h-full justify-between z-10">
                                    <div className="flex-grow flex flex-col justify-center">
                                        <blockquote className="text-white mb-8">
                                            <div className="text-5xl text-blue-200 absolute top-8 left-10 opacity-50">"</div>
                                            <p className="text-xl md:text-2xl leading-relaxed italic text-center px-10">
                                                {i18n.language === 'zh' ? testimonials[currentIndex].body.cn : testimonials[currentIndex].body.en}
                                            </p>
                                            <div className="text-5xl text-blue-200 absolute bottom-32 right-10 opacity-50">"</div>
                                        </blockquote>
                                    </div>

                                    {/* <div className="flex items-center justify-center gap-x-4 mt-6">
                                        <div className="overflow-hidden rounded-full bg-white/10 p-1 ring-2 ring-white/20">
                                            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                                <span className="text-white text-xl font-bold">{testimonials[currentIndex].author.name.charAt(0)}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="font-semibold text-xl text-white">{testimonials[currentIndex].author.name}</div>
                                            <div className="text-indigo-200 text-lg">
                                                {testimonials[currentIndex].author.role}
                                            </div>
                                        </div>
                                    </div> */}
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* 指示器 */}
                    <div className="flex justify-center mt-6 space-x-2">
                        {testimonials.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => updateIndex(index)}
                                className={`h-2 w-2 rounded-full transition-all duration-300 ${currentIndex === index
                                    ? 'bg-white w-8'
                                    : 'bg-white/30 hover:bg-white/50'
                                    }`}
                                aria-label={`转到推荐 ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Testimonials; 